import { Box, HeadingProps, TextProps } from '@wix/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { dataHooks } from '../../dataHooks';
import { AppContextProvider } from '../AppLoader';
import { ThemedHeading, ThemedText } from '../ThemedComponents';
import s from './DialogHeader.scss';
import { useHeadingSize } from './useHeadingSize';

export type DialogFlow = 'authPage' | 'secondaryFlow';

interface DialogHeaderProps {
  dataHook?: string;
  className?: string;
  maxWidth?: string;
}

// Tracks whether any auth screen header has already mounted this session, so we
// never move focus on the initial page load (which would steal the email
// field's autofocus).
let hasMountedDialogHeader = false;

const _DialogHeader: React.FC<DialogHeaderProps> = observer(
  ({ dataHook, children, className, maxWidth }) => {
    const {
      rootStore: {
        displayStore: { isVerticalLayout, titleSectionGap, isMobileFullScreen },
      },
    } = useContext(AppContextProvider);

    // Client-side navigation between auth screens (e.g. login <-> signup) leaves
    // keyboard/screen-reader focus orphaned on <body>. When a header mounts after
    // a transition, rescue focus onto its heading — but only if focus was lost to
    // <body>, so we never steal an autofocused input or the initial-load focus.
    useEffect(() => {
      const containerDataHook = dataHook ?? dataHooks.dialogHeader.container;
      const raf = requestAnimationFrame(() => {
        if (!hasMountedDialogHeader) {
          hasMountedDialogHeader = true;
          return;
        }
        const activeElement = document.activeElement;
        if (activeElement && activeElement !== document.body) {
          return;
        }
        const heading = document
          .querySelector(`[data-hook="${containerDataHook}"]`)
          ?.querySelector<HTMLElement>('h1, h2, h3, [role="heading"]');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
        }
      });
      return () => cancelAnimationFrame(raf);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <Box
        aria-live="assertive"
        aria-atomic="true"
        direction="vertical"
        gap={titleSectionGap}
        align="center"
        maxWidth={maxWidth}
        className={classNames(
          { [s.vertical]: isVerticalLayout },
          { [s.mobileFullScreen]: isMobileFullScreen },
          s.dialogHeaderContainer,
          className,
        )}
        dataHook={dataHook ?? dataHooks.dialogHeader.container}
      >
        {children}
      </Box>
    );
  },
);

export interface CustomHeadingProps extends HeadingProps {
  flow?: DialogFlow;
  customFontSize?: CustomFontSize;
}

export type CustomFontSize =
  | 'tiny'
  | 'small'
  | 'medium'
  | 'large'
  | 'xLarge'
  | 'xxLarge';

const Title: React.FC<CustomHeadingProps> = observer(
  ({ children, dataHook, className, flow, customFontSize, ...props }) => {
    const {
      rootStore: {
        displayStore: {
          isVerticalLayout,
          isMobile,
          isWixel,
          isMobileFullScreen,
          hasCustomTitleSectionGap,
        },
      },
    } = useContext(AppContextProvider);
    const { fontSize, as } = useHeadingSize(customFontSize, flow);

    return (
      <ThemedHeading
        aria-live="assertive"
        aria-atomic="true"
        dataHook={dataHook}
        className={classNames(s.mainTitle, className, {
          [s.mobile]: isMobile,
          [s.vertical]: isVerticalLayout,
          [s.wixel]: isWixel,
          [s.mobileFullScreen]: isMobileFullScreen,
          [s.gapControlledSpacing]: hasCustomTitleSectionGap,
        })}
        as={as}
        id={fontSize && s[`heading_${fontSize}`]}
        {...props}
      >
        {children}
      </ThemedHeading>
    );
  },
);

const SubTitleLarge: React.FC<{ dataHook?: string }> = observer(
  ({ children, dataHook }) => {
    const {
      rootStore: { displayStore },
    } = useContext(AppContextProvider);
    return displayStore.isMobile ? (
      <ThemedText weight="normal">{children}</ThemedText>
    ) : (
      <ThemedHeading appearance="H4" dataHook={dataHook}>
        {children}
      </ThemedHeading>
    );
  },
);

const SubTitleSmall: React.FC<TextProps & { maxWidth?: string }> = ({
  children,
  dataHook,
  maxWidth,
  className,
  ...props
}) => {
  const {
    rootStore: { displayStore },
  } = useContext(AppContextProvider);
  return (
    <Box maxWidth={maxWidth}>
      <ThemedText
        dataHook={dataHook}
        className={classNames(className, s.subTitleSmall, {
          [s.vertical]: displayStore.isVerticalLayout,
        })}
        {...props}
      >
        {children}
      </ThemedText>
    </Box>
  );
};

export const DialogHeader = Object.assign(_DialogHeader, {
  SubTitleLarge,
  SubTitleSmall,
  Title,
});
